import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, User, Trophy, Target, Clock, Activity, Calendar, Users } from 'lucide-react';
import './PlayerDetail.css';

function PlayerDetail({ token, teamId, playerId, nightMode, onBack, onTeamClick }) {
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchPlayerDetails();
  }, [teamId, playerId]);

  const fetchPlayerDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/teams/${teamId}/players/${playerId}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlayerData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Player details fetch error:', error);
      setError('Failed to fetch player details: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getMatchResult = (match) => {
    const isTeamA = match.teamA === playerData.team.name;
    const ourScore = isTeamA ? match.scoreA : match.scoreB;
    const theirScore = isTeamA ? match.scoreB : match.scoreA;
    
    if (ourScore > theirScore) return 'win';
    if (theirScore > ourScore) return 'loss';
    return 'tie';
  };

  const getFormClass = (result) => {
    switch(result) {
      case 'W': return 'form-win';
      case 'L': return 'form-loss';
      case 'T': return 'form-tie';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className={`player-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading">Loading player details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`player-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="player-detail-header">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={20} /> Back
          </button>
          <h1>Player Details</h1>
        </div>
        <div className="error-message">
          {error}
          <button onClick={fetchPlayerDetails} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!playerData) {
    return (
      <div className={`player-detail ${nightMode ? 'night-mode' : ''}`}>
        <div className="player-detail-header">
          <button onClick={onBack} className="back-btn">
            <ArrowLeft size={20} /> Back
          </button>
          <h1>Player Details</h1>
        </div>
        <div className="empty-state">
          <p>No data available for this player.</p>
        </div>
      </div>
    );
  }

  const winLossData = [
    { name: 'Wins', value: playerData.stats.wins, color: '#28a745' },
    { name: 'Losses', value: playerData.stats.losses, color: '#dc3545' },
    { name: 'Ties', value: playerData.stats.ties, color: '#ffc107' }
  ];

  const opponentsChartData = playerData.opponents.slice(0, 5).map(opponent => ({
    name: opponent.name.length > 12 ? opponent.name.substring(0, 12) + '...' : opponent.name,
    matches: opponent.matches,
    wins: opponent.wins,
    losses: opponent.losses,
    winRate: parseFloat(opponent.winPercentage)
  }));

  return (
    <div className={`player-detail ${nightMode ? 'night-mode' : ''}`}>
      <div className="player-detail-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft size={20} /> Back
        </button>
        <div className="player-title">
          <div className="player-info">
            <div className="player-icon">
              <User size={32} />
            </div>
            <div className="player-text">
              <h1>{playerData.player.name}</h1>
              <div className="player-meta">
                {playerData.player.number && (
                  <span className="player-number">#{playerData.player.number}</span>
                )}
                {playerData.player.position && (
                  <span className="player-position">{playerData.player.position}</span>
                )}
                <span 
                  className="team-link" 
                  onClick={() => onTeamClick && onTeamClick(playerData.team.name)}
                >
                  <Users size={16} />
                  {playerData.team.name}
                </span>
              </div>
            </div>
          </div>
          <span className="stats-subtitle">{playerData.stats.totalMatches} matches played</span>
        </div>
      </div>

      <div className="player-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          Match History
        </button>
        <button 
          className={`tab-btn ${activeTab === 'opponents' ? 'active' : ''}`}
          onClick={() => setActiveTab('opponents')}
        >
          vs Opponents
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="overview-tab">
          <div className="stats-summary">
            <div className="stat-card">
              <div className="stat-icon">
                <Trophy size={24} />
              </div>
              <div className="stat-content">
                <h3>{playerData.stats.totalMatches}</h3>
                <p>Total Matches</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon win">
                <Target size={24} />
              </div>
              <div className="stat-content">
                <h3>{playerData.stats.winPercentage}%</h3>
                <p>Win Rate</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Activity size={24} />
              </div>
              <div className="stat-content">
                <h3>{playerData.stats.avgPointsFor}</h3>
                <p>Avg Points For</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <h3>{formatTime(playerData.stats.totalTime)}</h3>
                <p>Total Play Time</p>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-container">
              <h3>Win/Loss Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={winLossData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {winLossData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3>Performance vs Top Opponents</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={opponentsChartData}>

                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="wins" fill="#28a745" name="Wins" />
                  <Bar dataKey="losses" fill="#dc3545" name="Losses" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="recent-form-container">
              <h3>Recent Form (Last 10 matches)</h3>
              <div className="form-display">
                {playerData.stats.recentForm.split('').map((result, index) => (
                  <span key={index} className={`form-indicator ${getFormClass(result)}`}>
                    {result}
                  </span>
                ))}
              </div>
              <p className="form-legend">
                <span className="form-win">W</span> = Win, 
                <span className="form-loss">L</span> = Loss, 
                <span className="form-tie">T</span> = Tie
              </p>
            </div>

            <div className="detailed-stats">
              <h3>Career Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Wins</span>
                  <span className="stat-value">{playerData.stats.wins}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Losses</span>
                  <span className="stat-value">{playerData.stats.losses}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Ties</span>
                  <span className="stat-value">{playerData.stats.ties}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Points Scored</span>
                  <span className="stat-value">{playerData.stats.pointsFor}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Points Conceded</span>
                  <span className="stat-value">{playerData.stats.pointsAgainst}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Point Differential</span>
                  <span className="stat-value">
                    {playerData.stats.pointsFor - playerData.stats.pointsAgainst >= 0 ? '+' : ''}
                    {playerData.stats.pointsFor - playerData.stats.pointsAgainst}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Points Conceded</span>
                  <span className="stat-value">{playerData.stats.avgPointsAgainst}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'matches' && (
        <div className="matches-tab">
          <h2>Match History</h2>
          <div className="matches-list">
            {playerData.matches.map((match, index) => {
              const result = getMatchResult(match);
              const isTeamA = match.teamA === playerData.team.name;
              const opponent = isTeamA ? match.teamB : match.teamA;
              const ourScore = isTeamA ? match.scoreA : match.scoreB;
              const theirScore = isTeamA ? match.scoreB : match.scoreA;
              
              return (
                <div key={index} className={`match-item ${result}`}>
                  <div className="match-date">
                    <Calendar size={16} />
                    {formatDate(match.date)}
                  </div>
                  <div className="match-teams">
                    <span className="team-name">{playerData.team.name}</span>
                    <span className="vs">vs</span>
                    <span className="opponent-name">{opponent}</span>
                  </div>
                  <div className="match-score">
                    <span className={`score ${result}`}>
                      {ourScore} - {theirScore}
                    </span>
                  </div>
                  <div className="match-time">
                    <Clock size={14} />
                    {formatTime(match.time)}
                  </div>
                  {match.matchGroup && (
                    <div className="match-group">
                      {match.matchGroup.name}
                    </div>
                  )}
                  {match.note && (
                    <div className="match-note">
                      {match.note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'opponents' && (
        <div className="opponents-tab">
          <h2>Performance vs Opponents</h2>
          <div className="opponents-table">
            <table>
              <thead>
                <tr>
                  <th>Opponent</th>
                  <th>Matches</th>
                  <th>Wins</th>
                  <th>Losses</th>
                  <th>Ties</th>
                  <th>Win %</th>
                </tr>
              </thead>
              <tbody>
                {playerData.opponents.map((opponent, index) => (
                  <tr key={index}>
                    <td className="opponent-name">{opponent.name}</td>
                    <td>{opponent.matches}</td>
                    <td className="wins">{opponent.wins}</td>
                    <td className="losses">{opponent.losses}</td>
                    <td className="ties">{opponent.ties}</td>
                    <td className="win-percentage">{opponent.winPercentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerDetail;
